import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { IFieldEditor, IFieldEditorPromptGroup } from '@/shared/types/fieldEditor';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { DealAssistantAISettingPromptSummaryContextGlobalDefaultSchema, promptGroupOutputStructureSchema } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FieldPromptEditorTemplate } from './fieldPromptEditorTemplate';
import DeleteAllPrompts from './modals/deleteAllPrompts';
import {
  CREATE_DEAL_ASSISTANT_AI_PROMPT_GROUP_MUTATION,
  DEAL_ASSISTANT_AI_PROMPT_SETTING_SUMMARY_CONTEXT_GLOBAL_DEFAULT_QUERY,
  UPDATE_DEAL_ASSISTANT_AI_PROMPT_GROUP_MUTATION
} from './queries';

const promptBannerDescription = 'Provide a prompt to for the AI Assistant to suggest an answer for this field.  Leave blank to skip AI suggestions.';

const defaultContextTemplateBannerDescription = 'The Context Template includes instructions for the AI that accompany the prompt.';
const defaultSummaryTemplateBannerDescription =
  'The initial AI response generally requires some stylistic cleanup.  The Summary Template provides instructions for this second step.';
const defaultStructuredOutputTemplateBannerDescription =
  'Use Structured Output to format the output as tables or key value pairs or enforce a strict, pre-defined structure.';

const customizeContextTemplateBannerDescription =
  'The Context Template includes instructions for the AI that accompany the prompt and content from the provided file (the “context”). The Context Template assembles a full prompt for the AI, which will include {context}. Be sure to provide this token in the template.';
const customizeSummaryTemplateBannerDescription =
  'The initial AI response generally requires some stylistic cleanup.  The Summary Template provides instructions for this second step. Tip: For better performance, wrap any instructions with the tags <instructions> and </instructions>.';
const customizeStructuredOutputTemplateBannerDescription =
  'The initial AI response generally requires some stylistic cleanup.  The Summary Template provides instructions for this second step. Tip: For better performance, wrap any instructions with the tags <instructions> and </instructions>.';

export interface IFieldPromptEditorForm {
  prompt: string;
  contextPrompt: string;
  summaryPrompt: string;
  outputStructure: string;
  useCustomContextPrompt: boolean;
  useCustomSummaryPrompt: boolean;
  useStructuredOutputTemplate: boolean;
}

const defaultFormValues: IFieldPromptEditorForm = {
  prompt: '',
  contextPrompt: '',
  summaryPrompt: '',
  outputStructure: '',
  useCustomContextPrompt: false,
  useCustomSummaryPrompt: false,
  useStructuredOutputTemplate: false
};

const globalAiPromptDefaultsInitialState: DealAssistantAISettingPromptSummaryContextGlobalDefaultSchema = {
  defaultContextPrompt: '',
  defaultSummaryPrompt: ''
};

interface FieldPromptEditorModalPageProps {
  currentRow: IFieldEditor | null;
  currentRowName: string;
  isDueDiligenceFunnelTierSectionField?: boolean;
  buttonControls?: {
    deletePromptSettings?: boolean;
  };
  refetch: () => Promise<ApolloQueryResult<any>>;
  savePromptGroup?: (promptGroup: IFieldEditorPromptGroup | null) => void;
  handleClose: () => void;
}

export const FieldPromptEditorModalPage = ({
  currentRow,
  currentRowName,
  isDueDiligenceFunnelTierSectionField = false,
  buttonControls,
  refetch,
  savePromptGroup,
  handleClose
}: FieldPromptEditorModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitted, isValid },
    reset,
    watch,
    handleSubmit
  } = useForm<IFieldPromptEditorForm>({
    mode: 'onChange',
    defaultValues: defaultFormValues
  });

  const [globalAiPromptDefaults, setGlobalAiPromptDefaults] =
    useState<DealAssistantAISettingPromptSummaryContextGlobalDefaultSchema>(globalAiPromptDefaultsInitialState);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [updatePromptGroup] = useMutation(UPDATE_DEAL_ASSISTANT_AI_PROMPT_GROUP_MUTATION);
  const [createPromptGroup] = useMutation(CREATE_DEAL_ASSISTANT_AI_PROMPT_GROUP_MUTATION);

  const { loading: defaultAiPromptsLoading } = useQuery<{
    dealAssistantAIPromptSettingSummaryContextGlobalDefault: DealAssistantAISettingPromptSummaryContextGlobalDefaultSchema;
  }>(DEAL_ASSISTANT_AI_PROMPT_SETTING_SUMMARY_CONTEXT_GLOBAL_DEFAULT_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setGlobalAiPromptDefaults(data?.dealAssistantAIPromptSettingSummaryContextGlobalDefault || globalAiPromptDefaultsInitialState);
    }
  });

  useEffect(() => {
    if (!currentRow?.promptGroup) return;

    reset({
      prompt: currentRow?.promptGroup?.prompts[0] ?? '',
      contextPrompt: currentRow?.promptGroup?.contextPrompt || '',
      summaryPrompt: currentRow?.promptGroup?.summaryPrompt || '',
      outputStructure: currentRow?.promptGroup?.outputStructure ? currentRow?.promptGroup?.outputStructure : '',
      useCustomContextPrompt: currentRow?.promptGroup?.useCustomContextPrompt,
      useCustomSummaryPrompt: currentRow?.promptGroup?.useCustomSummaryPrompt,
      useStructuredOutputTemplate: !!currentRow?.promptGroup?.outputStructure
    });
  }, [currentRow]);

  const saveValue = async (data: IFieldPromptEditorForm) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { prompt, contextPrompt, summaryPrompt, outputStructure, useCustomContextPrompt, useCustomSummaryPrompt, useStructuredOutputTemplate } =
        data;
      const savePromptGroupData: IFieldEditorPromptGroup = {
        id: '',
        prompts: [''],
        contextPrompt: '',
        summaryPrompt: '',
        outputStructure: '',
        useCustomContextPrompt: false,
        useCustomSummaryPrompt: false
      };

      if (useStructuredOutputTemplate) {
        const parsedOutputStructure = JSON.parse(outputStructure);
        const validationResult = promptGroupOutputStructureSchema.safeParse(parsedOutputStructure);

        if (!validationResult.success) {
          throw new Error(validationResult?.error?.message);
        }
      }

      if (!currentRow?.promptGroup?.id) {
        await createPromptGroup({
          variables: {
            data: {
              fieldId: currentRow?.fieldId,
              prompts: [prompt],
              ...(useCustomContextPrompt && {
                contextPrompt
              }),
              ...(useCustomSummaryPrompt && {
                summaryPrompt
              }),
              outputStructure: useStructuredOutputTemplate ? outputStructure : null,
              useCustomContextPrompt,
              useCustomSummaryPrompt,
              rubricTemplates: [],
              isDueDiligenceFunnelTierSectionField
            }
          },
          onCompleted: (data) => {
            const { id, prompts, summaryPrompt, contextPrompt, outputStructure, useCustomContextPrompt, useCustomSummaryPrompt } =
              data.createDealAssistantAIPromptGroup;

            Object.assign(savePromptGroupData, {
              id,
              prompts: [prompts[0]],
              contextPrompt,
              summaryPrompt,
              outputStructure,
              useCustomContextPrompt,
              useCustomSummaryPrompt
            });
          }
        });
      } else {
        await updatePromptGroup({
          variables: {
            data: {
              id: currentRow?.promptGroup?.id,
              prompts: [prompt],
              ...(useCustomContextPrompt && {
                contextPrompt
              }),
              ...(useCustomSummaryPrompt && {
                summaryPrompt
              }),
              outputStructure: useStructuredOutputTemplate ? outputStructure : null,
              useCustomContextPrompt,
              useCustomSummaryPrompt,
              rubricTemplates: []
            }
          },
          onCompleted: (data) => {
            const { id, prompts, summaryPrompt, contextPrompt, outputStructure, useCustomContextPrompt, useCustomSummaryPrompt } =
              data.updateDealAssistantAIPromptGroup;

            Object.assign(savePromptGroupData, {
              id,
              prompts: [prompts[0]],
              contextPrompt,
              summaryPrompt,
              outputStructure,
              useCustomContextPrompt,
              useCustomSummaryPrompt
            });
          }
        });
      }

      if (savePromptGroup) {
        savePromptGroup(savePromptGroupData);
      }

      handleClose();
      refetch();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (defaultAiPromptsLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {modalWindow.type === 'delete-prompts' && (
        <DeleteAllPrompts
          isOpen={modalWindow.isOpen}
          fieldName={currentRowName}
          promptGroupId={currentRow?.promptGroup?.id ?? ''}
          refetch={refetch}
          savePromptGroup={savePromptGroup}
          closeFieldPromptEditorModalPage={handleClose}
          onClose={() => setModalWindow({ type: '', isOpen: false })}
        />
      )}
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="" />} />
        <PageTitle title={currentRowName} />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <CustomPaddingWrap>
          <PromptEditorWrapper>
            <PromptField>
              <Banner
                title=""
                description={promptBannerDescription}
                icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
                bgColor={themeVar('layer.base')}
                color={themeVar('font.base')}
                dividerColor={themeVar('border.base')}
              />
              <Controller
                name="prompt"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    size="md"
                    errorText={error?.message}
                    limitCharactersTo={1000}
                    label="Prompt"
                  />
                )}
              />
            </PromptField>
            <FieldPromptEditorTemplate
              control={control}
              radioButtonName="useCustomContextPrompt"
              textareaName="contextPrompt"
              title="Context Template"
              textareaLabel="Custom Context Template"
              defaultAiPrompt={globalAiPromptDefaults.defaultContextPrompt}
              defaultBannerDescription={defaultContextTemplateBannerDescription}
              customizeBannerDescription={customizeContextTemplateBannerDescription}
              selectedOption={watch('useCustomContextPrompt') ? 'Customize' : 'Use default'}
              radioButtonItems={['Use default', 'Customize']}
            />
            <FieldPromptEditorTemplate
              control={control}
              radioButtonName="useCustomSummaryPrompt"
              textareaName="summaryPrompt"
              title="Summary Template"
              textareaLabel="Custom Summary Template"
              defaultAiPrompt={globalAiPromptDefaults.defaultSummaryPrompt}
              defaultBannerDescription={defaultSummaryTemplateBannerDescription}
              customizeBannerDescription={customizeSummaryTemplateBannerDescription}
              selectedOption={watch('useCustomSummaryPrompt') ? 'Customize' : 'Use default'}
              radioButtonItems={['Use default', 'Customize']}
            />
            <FieldPromptEditorTemplate
              control={control}
              radioButtonName="useStructuredOutputTemplate"
              textareaName="outputStructure"
              title="Structured Output Template"
              textareaLabel="Custom Structured Output Template"
              defaultBannerDescription={defaultStructuredOutputTemplateBannerDescription}
              customizeBannerDescription={customizeStructuredOutputTemplateBannerDescription}
              selectedOption={watch('useStructuredOutputTemplate') ? 'Customize' : 'None'}
              radioButtonItems={['None', 'Customize']}
            />
            <ButtonWrapper isMobile={isMobile}>
              <Button styleType="outline" onClick={handleClose} size="lg">
                Cancel
              </Button>
              {buttonControls?.deletePromptSettings && currentRow?.promptGroup && (
                <Button onClick={() => setModalWindow({ type: 'delete-prompts', isOpen: true })} styleType="outline" size="lg">
                  Delete Prompt Settings
                </Button>
              )}
              <Button styleType={isSubmitted && !isValid ? 'disabled' : 'default'} onClick={handleSubmit(saveValue)} size="lg">
                Save
              </Button>
            </ButtonWrapper>
          </PromptEditorWrapper>
        </CustomPaddingWrap>
      </MainWrap>
    </>
  );
};

const CustomPaddingWrap = styled(PaddingWrap)`
  padding-top: 50px;
  padding-bottom: 50px;
`;

const PromptEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 20px 60px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const PromptField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ButtonWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  padding: 20px 0;
  gap: 10px;
`;
